import type { JourneyPayload, SemanticEvent } from '../../../types/analyticstypes';
import { formatLatencyMs } from '../../../utils/analytics/format';

interface AgeCheck {
  productName: string;
  minAge: number;
  requestedTs: number;
  outcomeType: string;
  outcomeTs?: number;
}

const OUTCOME_TYPES = new Set([
  'age_approved',
  'age_auto_approved',
  'age_denied',
  'age_cancelled',
]);

function outcomeLabel(t: string): { label: string; cls: string } {
  switch (t) {
    case 'age_approved':
      return { label: 'Operator approved', cls: 'bg-green-50 text-green-700 border-green-200' };
    case 'age_auto_approved':
      return { label: 'Auto-approved', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
    case 'age_denied':
      return { label: 'Denied', cls: 'bg-red-50 text-red-700 border-red-200' };
    case 'age_cancelled':
      return { label: 'Cancelled', cls: 'bg-gray-50 text-gray-700 border-gray-200' };
    default:
      return { label: t, cls: 'bg-gray-50 text-gray-700 border-gray-200' };
  }
}

export default function AgeVerificationCard({ journey }: { journey: JourneyPayload }) {
  const events = journey.semanticEvents || [];
  const checks: AgeCheck[] = [];
  let auto = 0;

  for (let i = 0; i < events.length; i++) {
    const ev = events[i];
    if (ev.type === 'age_verify_requested') {
      let outcome: SemanticEvent | undefined;
      for (let j = i + 1; j < events.length; j++) {
        if (OUTCOME_TYPES.has(events[j].type as string)) {
          outcome = events[j];
          break;
        }
      }
      checks.push({
        productName: String(ev.data?.productName ?? 'Item'),
        minAge: Number(ev.data?.minAge) || 0,
        requestedTs: ev.ts,
        outcomeType: (outcome?.type as string) ?? 'age_pending',
        outcomeTs: outcome?.ts,
      });
    } else if (ev.type === 'age_auto_approved') {
      auto++;
      checks.push({
        productName: String(ev.data?.productName ?? 'Item'),
        minAge: Number(ev.data?.minAge) || 0,
        requestedTs: ev.ts,
        outcomeType: 'age_auto_approved',
        outcomeTs: ev.ts,
      });
    }
  }

  if (checks.length === 0) return null;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <h4 className="text-sm font-semibold text-gray-800 mb-3">Age Verification</h4>
      <ul className="space-y-2">
        {checks.map((c, i) => {
          const oc = outcomeLabel(c.outcomeType);
          const latency = c.outcomeTs ? c.outcomeTs - c.requestedTs : null;
          return (
            <li
              key={i}
              className="flex items-center justify-between gap-2 text-xs border-b border-gray-50 pb-1 last:border-b-0 last:pb-0"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-gray-400 font-mono w-4 shrink-0">{c.minAge}+</span>
                <span className="text-gray-800 truncate">{c.productName}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {latency != null && (
                  <span className="text-gray-400 font-mono text-[10px]">
                    {formatLatencyMs(latency)}
                  </span>
                )}
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${oc.cls}`}
                >
                  {oc.label}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      <p className="text-[10px] text-gray-400 mt-2">
        {checks.length} check{checks.length !== 1 ? 's' : ''}
        {auto > 0 && ` · ${auto} auto-approved`}
      </p>
    </div>
  );
}
